import { Injectable, Injector } from '@angular/core';
import {
    type BaseRequestOptionsArgs, type DataCount, type HttpResponse, type Params, type Rest,
    type TransferTableComponent, CORE_CONFIG, HttpService, I18nService,
    LocalStorageService, PermissionService, SessionStorageService,
} from '@dm/core';
import { STORAGE_KEY } from 'app/common/constants/index';
import { AuthService } from 'app/common/services/auth.service';
import { ToolService } from 'app/common/services/tool.service';
import { ProductConfig, SWITCH } from 'app/config/switch';
import { WorkLoadCreateTypeE, WorkLoadUsageTypeE } from 'app/feature/service/block-service/lun-group/luns.const';
import { type ModifyUserPreferenceModeParams } from 'app/feature/settings/users-security/user-role/user-role';
import { NEED_SEND_USER_SCOPE_LIST } from 'app/feature/settings/users-security/user-role/user-role.const';
import { GuideStorageKey } from 'app/feature/share/smart-assistant/smart-assistant.const';
import { ASSISTANT_WIDTH, FULL_WIDTH, SIMPLE_LEFT_MENU_WIDTH } from 'app/feature/simple-mode/simple-mode.const';
import { type Observable, of } from 'rxjs';

import { PreferenceModeE } from '../constants/simple-mode.const';
import { Theme } from '../constants/theme';
import { type Host, type Lun } from '../types/mo';
import { ThemesService } from './themes.service';

@Injectable({
    providedIn: 'root',
})
export class SimpleModeService {
    i18n = {
        simpleModeLabel: this.i18nService.get('home.simplifiedMode_lab'),
        standardModeLabel: this.i18nService.get('home.standardMode_lab'),
        modifyModeRest: (mode: string): string => this.i18nService.get('rest.modifyUserPreferenceMode_msg', [mode]),
    };

    constructor(
        private injector: Injector,
        private toolService: ToolService,
        private localStorageService: LocalStorageService,
        private permissionService: PermissionService,
        private sessionStorageService: SessionStorageService,
        private authService: AuthService,
        private httpService: HttpService,
        private i18nService: I18nService,
    ) { }

    /**
     * 设置极简模式
     */
    setSimpleMode(isSimpleMode: boolean, setLocalStorage = false): void {
        // 修改主题样式
        if (isSimpleMode !== SWITCH.IS_SIMPLE_MODE) {
            const themeService = this.injector.get(ThemesService, null);
            themeService.changeTheme(isSimpleMode ? Theme.SIMPLE_MODE : Theme.AUI);
        }

        // 设置全局开关
        SWITCH.IS_SIMPLE_MODE = isSimpleMode;
        CORE_CONFIG.isSimpleMode = isSimpleMode;

        if (setLocalStorage) {
            const simpleModeObj = this.localStorageService.get(STORAGE_KEY.SIMPLE_MOD) || {};
            const userId = CORE_CONFIG.userId;

            if (!simpleModeObj || !userId) {
                return;
            }

            simpleModeObj[userId] = isSimpleMode;
            this.localStorageService.set(STORAGE_KEY.SIMPLE_MOD, simpleModeObj);
        }
    }

    /**
     * 读取localstorage内容，初始化极简模式状态
     * @param userId 用户id，未进行过hash
     * @returns
     */
    initSimpleMode(userId: string, roleId: string): void {
        const simpleModeObj = this.localStorageService.get(STORAGE_KEY.SIMPLE_MOD);

        if (userId === undefined || roleId === undefined || !simpleModeObj) {
            return;
        }

        // 初始化role，判断是否超级管理员
        this.permissionService.setRoleId(roleId);
        if (!this.permissionService.isSuperAdmin()) {
            return;
        }

        // 判断是否入门级存储型号
        if (!ProductConfig.IsSupportSimpleMode) {
            return;
        }

        const authService = this.injector.get(AuthService, null);
        const hashedUserId = authService.getHashUserId(userId);

        if (Boolean(simpleModeObj[hashedUserId]) === true) {
            // 将从localstorage中读取的状态设置到服务中
            this.setSimpleMode(true, false);
        }
    }

    /**
     * 极简模式-将通过智能助手引导创建的对象放在前面
     */
    reverseOrder = (params: Params, objList: Lun[] | Host[]): Params => {
        if (!SWITCH.IS_SIMPLE_MODE || objList.length === 0) {
            return params;
        }

        // 如果指定了排序，则直接返回
        const queryParams = params;
        if (queryParams.sortby) {
            return queryParams;
        }

        // 极简模式下，LUN以创建时间倒序，主机以ID倒序
        queryParams.sort = Object.prototype.hasOwnProperty.call(objList[0], 'createTime') ? 'createTime' : 'ID';
        queryParams.sortby = 'd';
        return queryParams;
    };

    /**
     * 极简模式-自动选中通过智能助手引导创建的对象
     */
    autoSelectData = (dataRes: HttpResponse, countRes: HttpResponse, objList: any[], transferTable: TransferTableComponent): DataCount => {
        // 默认选中极简模式-智能助手引导下创建的对象
        if (SWITCH.IS_SIMPLE_MODE && dataRes.data) {
            const dataList = [];
            dataRes.data.forEach((obj: Lun | Host) => {
                if (objList.find((createdObj: Lun | Host) => obj.ID === createdObj.ID)) {
                    dataList.push(obj);
                }
            });
            transferTable.onLeftSelectionChange([...transferTable.leftSelection, ...dataList]);
        }

        return { data: dataRes.data || [], count: countRes.data.COUNT };
    };

    /**
     * 判断是否显示NEW标记
     */
    getNewTag = (ID: string, createdObjList: any[]): boolean => Boolean(createdObjList.find((createdObj: Lun | Host) => createdObj.ID === ID));

    /**
     * 入门级型号极简需求查询应用类型请求参数拼接 [标准模式、极简模式]
     */
    getWorkLoadTypeRest = (rest: Rest): Rest => {
        if (SWITCH.IS_SIMPLE_MODE) {
            /**
             * 1.过滤出极简模式下的内置应用类型
             * 2.过滤出自定义应用类型
             * 3.过滤出默认应用类型Default
             */
            rest.setGropFilters([
                [
                    { field: 'workloadMode', matchMode: '::', value: WorkLoadUsageTypeE.SIMPLE },
                ],
                [
                    { field: 'CREATETYPE', matchMode: '::', value: WorkLoadCreateTypeE.CUSTOM },
                ],
                [
                    { field: 'NAME', matchMode: '::', value: 'Default' },
                ],
                [
                    { field: 'NAME', matchMode: '::', value: 'Others' },
                ],
                [
                    { field: 'NAME', matchMode: '::', value: 'Video_Archive' },
                ],
            ]);
        } else {
            /**
             * 1.过滤出标准模式下的内置应用类型
             * 2.过滤出自定义应用类型
             */
            rest.setGropFilters([
                [
                    { field: 'workloadMode', matchMode: '::', value: WorkLoadUsageTypeE.ADVANCED },
                ],
                [
                    { field: 'CREATETYPE', matchMode: '::', value: WorkLoadCreateTypeE.CUSTOM },
                ],
            ]);
        }

        return rest;
    };

    /**
     * 根据智能助手和左侧菜单显示情况获取页面的宽度
     * @param isLeftMenuExpand 左侧菜单是否展开
     */
    getSimplePageWidth(isLeftMenuExpand: boolean): string {
        // 计算智能助手宽度
        const guideInfo = this.sessionStorageService.get(GuideStorageKey.guideConfig) || {};
        const assistantWidth = guideInfo[GuideStorageKey.showSmartAssistant] ? ASSISTANT_WIDTH : '0px';

        // 计算左侧菜单的宽度
        const leftMenuWidth = isLeftMenuExpand ? SIMPLE_LEFT_MENU_WIDTH.MAX : SIMPLE_LEFT_MENU_WIDTH.MIN;

        return `calc(${FULL_WIDTH} - ${assistantWidth} - ${leftMenuWidth})`;
    }

    /**
     * 通过接口修改用户偏好模式
     */
    modifyUserPreferenceMode(isSimpleMode: boolean, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        // 根据用户ID修改用户偏好模式
        const userInfo = this.authService.getUserInfo();
        if (!userInfo.userid) {
            return of(null);
        }

        const rest = this.httpService.makeUrl('user.put').format({ id: userInfo.userid });
        const preferenceMode = isSimpleMode ? PreferenceModeE.Simple : PreferenceModeE.Standard;
        const option = {
            name: this.i18n.modifyModeRest(preferenceMode === PreferenceModeE.Simple ?
                this.i18n.simpleModeLabel :
                this.i18n.standardModeLabel,
            ),
            doSuccess: false,
            ...opt,
        };

        const params: ModifyUserPreferenceModeParams = { preferenceMode };

        if (NEED_SEND_USER_SCOPE_LIST.includes(Number(userInfo.userscope))) {
            params.SCOPE = Number(userInfo.userscope);
        }

        return this.httpService.put(rest, params, option);
    }
}
